import React, { useEffect, useState, useCallback } from 'react';
import { cobrosExpensasApi, expensasPortalApi } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { Inbox, ChevronDown, ChevronUp, FileText, CheckCircle2, XCircle, AlertTriangle, Loader2, Clock } from 'lucide-react';

const fmtMoney = (n) => `$ ${Math.round(Number(n || 0)).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
const fmtFecha = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-');
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : s;
};
const ESTADO = {
  en_revision: { label: 'En revisión', cls: 'bg-violet-50 text-violet-700 border-violet-200', icon: Clock },
  aprobado: { label: 'Aprobado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  rechazado: { label: 'Rechazado', cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle },
  correccion: { label: 'Requiere corrección', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
};
const MEDIO_LABEL = {
  transferencia: 'Transferencia', efectivo: 'Efectivo', deposito: 'Depósito',
  cheque: 'Cheque', mercado_pago: 'Mercado Pago', otro: 'Otro',
};

export function PagosInformadosBandeja({ edificioId, periodoId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accion, setAccion] = useState(null); // { item, tipo: 'rechazar'|'correccion' }
  const [motivo, setMotivo] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchCount = useCallback(async () => {
    if (!edificioId || !periodoId) return;
    try {
      const r = await cobrosExpensasApi.countPagosInformados({ edificio_id: edificioId, periodo_id: periodoId });
      setCount(r.data?.en_revision || 0);
    } catch { /* noop */ }
  }, [edificioId, periodoId]);

  const fetchList = useCallback(async () => {
    if (!edificioId || !periodoId) return;
    setLoading(true);
    try {
      const r = await cobrosExpensasApi.listPagosInformados({ edificio_id: edificioId, periodo_id: periodoId });
      setItems(r.data || []);
    } catch {
      toast.error('No se pudieron cargar los pagos informados');
    } finally { setLoading(false); }
  }, [edificioId, periodoId]);

  useEffect(() => { fetchCount(); }, [fetchCount]);
  useEffect(() => { if (open) fetchList(); }, [open, fetchList]);

  const refresh = () => { fetchCount(); fetchList(); onChanged && onChanged(); };

  const aprobar = async (item) => {
    setBusyId(item.pago_informado_id);
    try {
      await cobrosExpensasApi.aprobarPagoInformado(item.pago_informado_id);
      toast.success('Pago aprobado e impactado en Cobros');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aprobar');
    } finally { setBusyId(null); }
  };

  const confirmarAccion = async () => {
    if (!motivo.trim()) return toast.error('Indicá un motivo');
    setBusyId(accion.item.pago_informado_id);
    try {
      if (accion.tipo === 'rechazar') await cobrosExpensasApi.rechazarPagoInformado(accion.item.pago_informado_id, motivo.trim());
      else await cobrosExpensasApi.pedirCorreccionPagoInformado(accion.item.pago_informado_id, motivo.trim());
      toast.success(accion.tipo === 'rechazar' ? 'Pago rechazado' : 'Se pidió corrección');
      setAccion(null); setMotivo('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo procesar');
    } finally { setBusyId(null); }
  };

  if (!periodoId) return null;

  return (
    <div className="bg-white rounded-lg border border-slate-200" data-testid="pagos-informados-bandeja">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3" data-testid="bandeja-toggle">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 text-violet-600" />
          <span className="text-sm font-semibold text-slate-800">Pagos informados por propietarios</span>
          {count > 0 && (
            <span className="text-[11px] font-bold text-white bg-violet-600 rounded-full px-2 py-0.5" data-testid="bandeja-count">{count} en revisión</span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-3">
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No hay pagos informados para este período.</p>
          ) : (
            <div className="space-y-2">
              {items.map((it) => {
                const cfg = ESTADO[it.estado] || ESTADO.en_revision;
                const Icon = cfg.icon;
                const pendiente = it.estado === 'en_revision' || it.estado === 'correccion';
                return (
                  <div key={it.pago_informado_id} className="border border-slate-200 rounded-lg p-3" data-testid={`pi-${it.pago_informado_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 text-sm">{it.unidad_nombre} · {fmtMoney(it.monto)}</div>
                        <div className="text-xs text-slate-500">
                          {it.propietario_nombre} · {MEDIO_LABEL[it.metodo_pago] || it.metodo_pago} · pago {fmtFecha(it.fecha_pago)}
                        </div>
                        {it.comentario && <div className="text-xs text-slate-600 mt-1">“{it.comentario}”</div>}
                        {it.revision?.motivo && it.estado !== 'aprobado' && (
                          <div className="text-xs text-rose-600 mt-0.5">Motivo: {it.revision.motivo}</div>
                        )}
                        {it.comprobante && (
                          <a href={expensasPortalApi.comprobanteUrl(it.pago_informado_id)} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mt-1" data-testid={`pi-comprobante-${it.pago_informado_id}`}>
                            <FileText className="w-3 h-3" /> Ver comprobante
                          </a>
                        )}
                      </div>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${cfg.cls} flex-shrink-0`}>
                        <Icon className="w-3 h-3" /> {cfg.label}
                      </span>
                    </div>
                    {pendiente && (
                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-slate-100">
                        <Button size="sm" disabled={busyId === it.pago_informado_id} onClick={() => aprobar(it)}
                          className="bg-emerald-600 hover:bg-emerald-700" data-testid={`pi-aprobar-${it.pago_informado_id}`}>
                          {busyId === it.pago_informado_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Aprobar
                        </Button>
                        <Button size="sm" variant="outline" disabled={busyId === it.pago_informado_id}
                          onClick={() => { setAccion({ item: it, tipo: 'correccion' }); setMotivo(''); }} data-testid={`pi-correccion-${it.pago_informado_id}`}>
                          <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Pedir corrección
                        </Button>
                        <Button size="sm" variant="outline" disabled={busyId === it.pago_informado_id}
                          onClick={() => { setAccion({ item: it, tipo: 'rechazar' }); setMotivo(''); }}
                          className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`pi-rechazar-${it.pago_informado_id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Rechazar
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!accion} onOpenChange={(o) => !o && setAccion(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{accion?.tipo === 'rechazar' ? 'Rechazar pago' : 'Pedir corrección'}</DialogTitle>
          </DialogHeader>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
            placeholder="Motivo (lo verá el propietario)…" data-testid="pi-motivo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccion(null)}>Cancelar</Button>
            <Button onClick={confirmarAccion} disabled={busyId} data-testid="pi-confirmar"
              className={accion?.tipo === 'rechazar' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PagosInformadosBandeja;
